import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ConfirmModal, useToast } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { categorySuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { CcmMenu, CcmMenuButton, CcmMenuDivider } from './CcmMenu';

export interface CategoryTreeNode {
  id: string;
  name: string;
  status: string;
  coursesCount: number;
  children?: CategoryTreeNode[];
}

interface CategoryListItemProps {
  category: CategoryTreeNode;
  selectedId: string | null;
  depth?: number;
  onSelect: (id: string) => void;
}

export function CategoryListItem({ category, selectedId, depth = 0, onSelect }: CategoryListItemProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.CATEGORIAS_EDITAR);
  const canDelete = hasPermission(PERMISSIONS.CATEGORIAS_ELIMINAR);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isSelected = selectedId === category.id;
  const isVisible = category.status === 'active';

  const toggleStatus = useMutation({
    mutationFn: (status: string) => api(`/categories/${category.id}`, { method: 'PATCH', body: { status } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['categories'] }),
  });

  const removeCategory = useMutation({
    mutationFn: () => api(`/categories/${category.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteOpen(false);
      toast(categorySuccessToast('delete'));
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar la categoría'));
    },
  });

  return (
    <li className="ccm-category-item" style={{ ['--ccm-depth' as string]: depth }}>
      <div
        className={`ccm-category-row group ${isSelected ? 'ccm-category-row--selected' : ''} ${!isVisible ? 'ccm-category-row--hidden' : ''}`}
      >
        <button
          type="button"
          onClick={() => onSelect(category.id)}
          className="ccm-category-row__main min-w-0 flex-1 border-0 bg-transparent p-0 text-left"
        >
          <span className="material-symbols-outlined ccm-category-row__folder" aria-hidden="true">
            folder
          </span>
          <span className="ccm-category-row__name">{category.name}</span>
        </button>

        <div className="ccm-category-row__meta">
          <span className="ccm-category-row__count" title={`${category.coursesCount} cursos`}>
            <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
              inventory_2
            </span>
            {category.coursesCount}
          </span>

          <div className="ccm-category-row__actions">
            {canEdit && (
              <Link
                to={`/categorias/${category.id}/edit`}
                className="ccm-icon-btn"
                title="Ver detalle"
                aria-label={`Ver detalle de ${category.name}`}
              >
                <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
                  visibility
                </span>
              </Link>
            )}

            {canEdit && (
              <CcmMenu
                trigger={
                  <button type="button" className="ccm-icon-btn" title="Opciones" aria-label={`Opciones de ${category.name}`}>
                    <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
                      settings
                    </span>
                  </button>
                }
              >
                <Link to={`/categorias/${category.id}/edit`} className="ccm-menu-item">
                  Editar
                </Link>
                {isVisible ? (
                  <CcmMenuButton onClick={() => toggleStatus.mutate('inactive')}>Ocultar categoría</CcmMenuButton>
                ) : (
                  <CcmMenuButton onClick={() => toggleStatus.mutate('active')}>Mostrar categoría</CcmMenuButton>
                )}
                {canDelete && (
                  <>
                    <CcmMenuDivider />
                    <CcmMenuButton danger onClick={() => setDeleteOpen(true)}>
                      Eliminar
                    </CcmMenuButton>
                  </>
                )}
              </CcmMenu>
            )}
          </div>
        </div>
      </div>

      {category.children && category.children.length > 0 && (
        <ul className="ccm-category-children">
          {category.children.map((child) => (
            <CategoryListItem
              key={child.id}
              category={child}
              selectedId={selectedId}
              depth={depth + 1}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}

      <ConfirmModal
        open={deleteOpen}
        title="Eliminar categoría"
        description={`¿Está seguro de eliminar la categoría «${category.name}»? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        loading={removeCategory.isPending}
        onConfirm={() => removeCategory.mutate()}
        onClose={() => setDeleteOpen(false)}
      />
    </li>
  );
}
